import { Polygon } from "geojson";
import { LngLat } from "mapbox-gl";
import { useMemo } from "react";
import { useMap } from "react-map-gl";
import { useDrawMapStore, useMapMarkerStore, usePipedriveStore, usePropertyStore } from "..";
import { showErrorToast, showSuccessToast } from "../components/ToastContainer";
import { satelliteScreenshotFilename } from "../interfaces/IAttachment";
import IDeal, { IDealState } from "../interfaces/IDeal";
import GeometryService from "../services/geometryService";
import useCurrentOrFallbackPipedriveUserId from "./useCurrentOrFallbackPipedriveUserId";

const useDealUpdate = (): (deal: IDeal, dealState: IDealState, satelliteScreenshot?: File | null) => Promise<void> => {
    const map = useMap().map!;
    const pipedriveStore = usePipedriveStore();
    const drawMapStore = useDrawMapStore();
    const mapMarkerStore = useMapMarkerStore();
    const propertyStore = usePropertyStore();
    const geometryService = useMemo(() => new GeometryService(), []);
    const pipedriveUserId = useCurrentOrFallbackPipedriveUserId();

    return async (deal: IDeal, dealState: IDealState, satelliteScreenshot?: File | null): Promise<void> => {
        try {
            const { latitude, longitude } = mapMarkerStore.coordinates!;
            const coordinate = new LngLat(longitude, latitude);
            await propertyStore.fetchProperty(coordinate, map);
            const regridId = propertyStore.getProperty(coordinate)?.regridId ?? deal.regridId;

            await pipedriveStore.updateDeal({
                ...deal,
                ...dealState,
                name: dealState.name!,
                value: dealState.value!,
                status: dealState.status!,
                dealStageId: dealState.dealStageId!,
                assignedToUserId: dealState.assignedToUserId!,
                contactPersonId: await pipedriveStore.getOrCreatePipedrivePersonId(dealState.contactPersonName),
                contactCompanyId: await pipedriveStore.getOrCreatePipedriveOrganizationId(dealState.contactCompanyName),
                brokerId: await pipedriveStore.getOrCreatePipedrivePersonId(dealState.brokerName),
                propertyOwnerId: await pipedriveStore.getOrCreatePipedrivePersonId(dealState.propertyOwnerName),
                lat: latitude,
                lon: longitude,
                regridId: regridId,
                dealShapes: dealState.dealShapeApns!.map((apn, index) => ({
                    apn: apn,
                    points: geometryService.mapPolygonToShape(drawMapStore.result!.features[index].geometry as Polygon).points,
                })),
            });

            if (dealState.lostComment?.trim())
                await pipedriveStore.createDealNote(deal.id, dealState.lostComment, pipedriveUserId);

            if (satelliteScreenshot) {
                const dealFiles = await pipedriveStore.fetchDealFiles(deal.id);
                const currentScreenshot = dealFiles.find(f => f.name === satelliteScreenshotFilename);
                if (currentScreenshot) {
                    await pipedriveStore.deleteDealFile(currentScreenshot.id, deal.id);
                }
                await pipedriveStore.createDealFile(satelliteScreenshot, satelliteScreenshot.name, deal.id);
            }

            showSuccessToast();
        } catch (e) {
            showErrorToast();
            throw e;
        }
    };
};

export default useDealUpdate;